import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_2,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_6,
} from './operators';

/**
 * Triggers/actions listed here stay supported server-side but are hidden from the
 * dashboard builder until the product flow is complete (integrations, UX, etc.).
 */
export const AUTOMATION_DASHBOARD_HIDDEN_EVENT_KEYS = new Set([
  'tiktok_messaging_ad',
  'call_ended',
]);

/** Actions available in the workflow canvas “Add Steps” menu (order = menu order). */
export const WORKFLOW_CANVAS_ADD_STEP_KEYS = [
  'send_message',
  'ask_question',
  'assign_agent',
  'branch',
  'update_contact_attribute',
  'update_contact_tag',
  'open_conversation',
  'resolve_conversation',
];

export const AUTOMATION_DASHBOARD_HIDDEN_ACTION_KEYS = new Set([
  'wait',
  'ask_question',
  'branch',
  'jump_step',
  'date_time_window',
  'http_request',
  'google_sheets_add_row',
  'send_tiktok_lower_funnel_event',
  'ai_objective_legacy',
]);

const CONVERSATION_CONDITIONS = [
  {
    key: 'browser_language',
    name: 'BROWSER_LANGUAGE',
    inputType: 'search_select',
    filterOperators: OPERATOR_TYPES_1,
  },
  {
    key: 'email',
    name: 'EMAIL',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_2,
  },
  {
    key: 'mail_subject',
    name: 'MAIL_SUBJECT',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_2,
  },
  {
    key: 'country_code',
    name: 'COUNTRY_NAME',
    inputType: 'search_select',
    filterOperators: OPERATOR_TYPES_1,
  },
  {
    key: 'referer',
    name: 'REFERER_LINK',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_2,
  },
  {
    key: 'assignee_id',
    name: 'ASSIGNEE_NAME',
    inputType: 'search_select',
    filterOperators: OPERATOR_TYPES_3,
  },
  {
    key: 'phone_number',
    name: 'PHONE_NUMBER',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_6,
  },
  {
    key: 'team_id',
    name: 'TEAM_NAME',
    inputType: 'search_select',
    filterOperators: OPERATOR_TYPES_3,
  },
  {
    key: 'inbox_id',
    name: 'INBOX',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_1,
  },
  {
    key: 'conversation_language',
    name: 'CONVERSATION_LANGUAGE',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_1,
  },
  {
    key: 'priority',
    name: 'PRIORITY',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_1,
  },
  {
    key: 'labels',
    name: 'LABELS',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_3,
  },
  {
    key: 'status',
    name: 'STATUS',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_1,
  },
];

const CONTACT_CONDITIONS = [
  {
    key: 'email',
    name: 'EMAIL',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_2,
  },
  {
    key: 'phone_number',
    name: 'PHONE_NUMBER',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_6,
  },
  {
    key: 'name',
    name: 'NAME',
    inputType: 'plain_text',
    filterOperators: OPERATOR_TYPES_2,
  },
];

const MINIMAL_CONDITIONS = [
  {
    key: 'inbox_id',
    name: 'INBOX',
    inputType: 'multi_select',
    filterOperators: OPERATOR_TYPES_1,
  },
];

const STANDARD_ACTIONS_ALL = [
  { key: 'assign_agent', name: 'ASSIGN_AGENT' },
  { key: 'assign_team', name: 'ASSIGN_TEAM' },
  { key: 'remove_assigned_agent', name: 'REMOVE_ASSIGNED_AGENT' },
  { key: 'remove_assigned_team', name: 'REMOVE_ASSIGNED_TEAM' },
  { key: 'add_label', name: 'ADD_LABEL' },
  { key: 'remove_label', name: 'REMOVE_LABEL' },
  { key: 'send_email_to_team', name: 'SEND_EMAIL_TO_TEAM' },
  { key: 'send_email_transcript', name: 'SEND_EMAIL_TRANSCRIPT' },
  { key: 'mute_conversation', name: 'MUTE_CONVERSATION' },
  { key: 'snooze_conversation', name: 'SNOOZE_CONVERSATION' },
  { key: 'resolve_conversation', name: 'RESOLVE_CONVERSATION' },
  { key: 'open_conversation', name: 'OPEN_CONVERSATION' },
  { key: 'pending_conversation', name: 'PENDING_CONVERSATION' },
  { key: 'send_webhook_event', name: 'SEND_WEBHOOK_EVENT' },
  { key: 'send_attachment', name: 'SEND_ATTACHMENT' },
  { key: 'send_message', name: 'SEND_MESSAGE' },
  { key: 'add_private_note', name: 'ADD_PRIVATE_NOTE' },
  { key: 'change_priority', name: 'CHANGE_PRIORITY' },
  { key: 'wait', name: 'WAIT' },
  { key: 'ask_question', name: 'ASK_QUESTION' },
  { key: 'branch', name: 'BRANCH' },
  { key: 'jump_step', name: 'JUMP_STEP' },
  { key: 'date_time_window', name: 'DATE_TIME_WINDOW' },
  { key: 'http_request', name: 'HTTP_REQUEST' },
  { key: 'google_sheets_add_row', name: 'GOOGLE_SHEETS_ADD_ROW' },
  {
    key: 'send_tiktok_lower_funnel_event',
    name: 'SEND_TIKTOK_LOWER_FUNNEL_EVENT',
  },
  { key: 'ai_objective_legacy', name: 'AI_OBJECTIVE_LEGACY' },
  { key: 'update_contact_attribute', name: 'UPDATE_CONTACT_ATTRIBUTE' },
  { key: 'update_contact_tag', name: 'UPDATE_CONTACT_TAG' },
  { key: 'update_lifecycle', name: 'UPDATE_LIFECYCLE' },
  { key: 'trigger_automation', name: 'TRIGGER_AUTOMATION' },
  { key: 'send_api_event', name: 'SEND_API_EVENT' },
  { key: 'assign_ai_agent', name: 'ASSIGN_AI_AGENT' },
  { key: 'add_sla', name: 'ADD_SLA' },
];

const STANDARD_ACTIONS_DASHBOARD = STANDARD_ACTIONS_ALL.filter(
  ({ key }) => !AUTOMATION_DASHBOARD_HIDDEN_ACTION_KEYS.has(key)
);

const flow = (conditions, actions = STANDARD_ACTIONS_DASHBOARD) => ({
  conditions,
  actions,
});

/** Trigger conditions limited to inbox filter only (workflow builder). */
export const AUTOMATION_INBOX_ONLY_CONDITION_EVENT_KEYS = new Set([
  'conversation_opened',
]);

export const AUTOMATIONS = {
  conversation_opened: flow(MINIMAL_CONDITIONS),
  conversation_closed: flow(CONVERSATION_CONDITIONS),
  click_to_chat_ad: flow(CONVERSATION_CONDITIONS),
  tiktok_messaging_ad: flow(CONVERSATION_CONDITIONS),
  call_ended: flow(CONVERSATION_CONDITIONS),
  contact_field_updated: flow(CONTACT_CONDITIONS),
  contact_tag_updated: flow(CONTACT_CONDITIONS),
  lifecycle_updated: flow(CONTACT_CONDITIONS),
  manual: flow(MINIMAL_CONDITIONS),
  incoming_webhook: flow(MINIMAL_CONDITIONS),
};

export const AUTOMATION_RULE_EVENTS_ALL = [
  { key: 'conversation_opened', value: 'CONVERSATION_OPENED' },
  { key: 'conversation_closed', value: 'CONVERSATION_CLOSED' },
  { key: 'contact_field_updated', value: 'CONTACT_FIELD_UPDATED' },
  { key: 'contact_tag_updated', value: 'CONTACT_TAG_UPDATED' },
  { key: 'manual', value: 'MANUAL' },
  { key: 'incoming_webhook', value: 'INCOMING_WEBHOOK' },
  { key: 'click_to_chat_ad', value: 'CLICK_TO_CHAT_AD' },
  { key: 'tiktok_messaging_ad', value: 'TIKTOK_MESSAGING_AD' },
  { key: 'lifecycle_updated', value: 'LIFECYCLE_UPDATED' },
  { key: 'call_ended', value: 'CALL_ENDED' },
];

export const AUTOMATION_RULE_EVENTS = AUTOMATION_RULE_EVENTS_ALL.filter(
  ({ key }) => !AUTOMATION_DASHBOARD_HIDDEN_EVENT_KEYS.has(key)
);

export const AUTOMATION_ACTION_TYPES_ALL = [
  { key: 'assign_agent', label: 'ASSIGN_AGENT', inputType: 'search_select' },
  { key: 'assign_team', label: 'ASSIGN_TEAM', inputType: 'search_select' },
  {
    key: 'remove_assigned_agent',
    label: 'REMOVE_ASSIGNED_AGENT',
    inputType: null,
  },
  {
    key: 'remove_assigned_team',
    label: 'REMOVE_ASSIGNED_TEAM',
    inputType: null,
  },
  { key: 'add_label', label: 'ADD_LABEL', inputType: 'multi_select' },
  { key: 'remove_label', label: 'REMOVE_LABEL', inputType: 'multi_select' },
  {
    key: 'send_email_to_team',
    label: 'SEND_EMAIL_TO_TEAM',
    inputType: 'team_message',
  },
  {
    key: 'send_email_transcript',
    label: 'SEND_EMAIL_TRANSCRIPT',
    inputType: 'email',
  },
  { key: 'mute_conversation', label: 'MUTE_CONVERSATION', inputType: null },
  { key: 'snooze_conversation', label: 'SNOOZE_CONVERSATION', inputType: null },
  {
    key: 'resolve_conversation',
    label: 'RESOLVE_CONVERSATION',
    inputType: null,
  },
  { key: 'open_conversation', label: 'OPEN_CONVERSATION', inputType: null },
  {
    key: 'pending_conversation',
    label: 'PENDING_CONVERSATION',
    inputType: null,
  },
  { key: 'send_webhook_event', label: 'SEND_WEBHOOK_EVENT', inputType: 'url' },
  { key: 'send_attachment', label: 'SEND_ATTACHMENT', inputType: 'attachment' },
  { key: 'send_message', label: 'SEND_MESSAGE', inputType: 'textarea' },
  { key: 'add_private_note', label: 'ADD_PRIVATE_NOTE', inputType: 'textarea' },
  {
    key: 'change_priority',
    label: 'CHANGE_PRIORITY',
    inputType: 'search_select',
  },
  { key: 'add_sla', label: 'ADD_SLA', inputType: 'search_select' },
  { key: 'wait', label: 'WAIT', inputType: 'textarea' },
  { key: 'ask_question', label: 'ASK_QUESTION', inputType: 'textarea' },
  { key: 'branch', label: 'BRANCH', inputType: 'textarea' },
  { key: 'jump_step', label: 'JUMP_STEP', inputType: 'textarea' },
  { key: 'date_time_window', label: 'DATE_TIME_WINDOW', inputType: 'textarea' },
  { key: 'http_request', label: 'HTTP_REQUEST', inputType: 'textarea' },
  {
    key: 'google_sheets_add_row',
    label: 'GOOGLE_SHEETS_ADD_ROW',
    inputType: null,
  },
  {
    key: 'send_tiktok_lower_funnel_event',
    label: 'SEND_TIKTOK_LOWER_FUNNEL_EVENT',
    inputType: 'textarea',
  },
  {
    key: 'ai_objective_legacy',
    label: 'AI_OBJECTIVE_LEGACY',
    inputType: 'search_select',
  },
  {
    key: 'update_contact_attribute',
    label: 'UPDATE_CONTACT_ATTRIBUTE',
    inputType: 'textarea',
  },
  {
    key: 'update_contact_tag',
    label: 'UPDATE_CONTACT_TAG',
    inputType: 'textarea',
  },
  {
    key: 'update_lifecycle',
    label: 'UPDATE_LIFECYCLE',
    inputType: 'search_select',
  },
  {
    key: 'trigger_automation',
    label: 'TRIGGER_AUTOMATION',
    inputType: 'textarea',
  },
  { key: 'send_api_event', label: 'SEND_API_EVENT', inputType: 'textarea' },
  {
    key: 'assign_ai_agent',
    label: 'ASSIGN_AI_AGENT',
    inputType: 'search_select',
  },
];

export const AUTOMATION_ACTION_TYPES = AUTOMATION_ACTION_TYPES_ALL.filter(
  ({ key }) => !AUTOMATION_DASHBOARD_HIDDEN_ACTION_KEYS.has(key)
);
